import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-lg text-foreground-600 mb-4">找不到该漫画</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: `${comic.title} - 八仔漫画网`,
          description: comic.description,
          image: comic.cover,
          author: { '@type': 'Person', name: comic.author },
          datePublished: '2020-01-01',
          dateModified: comic.chapters[0]?.updatedAt || '2026-06-28',
          inLanguage: 'zh-CN',
          publisher: {
            '@type': 'Organization',
            name: '八仔漫画网',
            logo: {
              '@type': 'ImageObject',
              url: 'http://jisutxt.com/logo.png',
            },
          },
          genre: comic.category,
          keywords: comic.tags.join(', '),
          wordCount: comic.description.length,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `http://jisutxt.com/comic/${comic.slug}`,
          },
        }}
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: t('breadcrumb.home'),
              item: 'http://jisutxt.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: t('nav.category'),
              item: 'http://jisutxt.com/category/all',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: comic.category,
              item: `http://jisutxt.com/category/${comic.categorySlug}`,
            },
            {
              '@type': 'ListItem',
              position: 4,
              name: comic.title,
              item: `http://jisutxt.com/comic/${comic.slug}`,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: t('nav.category'), href: '/category/all' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
                <div className="lg:w-[350px] flex-shrink-0">
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] lg:sticky lg:top-24">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} 漫画封面`}
                      title={`${comic.title} - 八仔漫画网免费阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      comic.status === 'ongoing'
                        ? 'bg-accent-100 text-accent-800'
                        : 'bg-secondary-100 text-secondary-800'
                    }`}>
                      {comic.status === 'ongoing' ? t('comic.ongoing') : t('comic.completed')}
                    </span>
                    <div className="flex items-center gap-1 text-accent-500">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-sm"></i>
                      </div>
                      <span className="text-sm font-semibold">{comic.rating}</span>
                    </div>
                    <span className="text-xs text-foreground-400">{comic.views.toLocaleString()} 次阅读</span>
                  </div>

                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                    {comic.title}
                  </h1>

                  <div className="flex items-center gap-4 mb-5 text-sm text-foreground-600">
                    <span>{t('comic.author')}：{comic.author}</span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs bg-secondary-50 text-secondary-700 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-2xl">
                    {comic.description}
                  </p>

                  <div className="flex items-center gap-3 mb-10">
                    <Link
                      to={`/comic/${comic.slug}/chapter/1`}
                      className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                    >
                      {t('comic.startReading')}
                      <div className="w-4 h-4 flex items-center justify-center ml-2">
                        <i className="ri-play-circle-line text-sm"></i>
                      </div>
                    </Link>
                    <Link
                      to={`/comic/${comic.slug}/chapter/${comic.chapters.length}`}
                      className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold border border-foreground-300 text-foreground-700 hover:bg-foreground-100 transition-all whitespace-nowrap cursor-pointer"
                    >
                      {t('comic.latestChapter')}
                    </Link>
                  </div>

                  <div>
                    <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-list-check text-primary-500"></i>
                      </div>
                      {t('comic.chapters')} ({comic.chapters.length})
                    </h2>
                    <div className="bg-background-100 rounded-lg divide-y divide-background-200/70 max-h-[400px] overflow-y-auto">
                      {comic.chapters.map((chapter) => (
                        <Link
                          key={chapter.id}
                          to={`/comic/${comic.slug}/chapter/${chapter.id}`}
                          className="flex items-center justify-between px-4 py-3 hover:bg-primary-50 transition-colors cursor-pointer"
                        >
                          <span className="text-sm font-medium text-foreground-800">
                            {chapter.title}
                          </span>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">
                            {chapter.updatedAt}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}